import * as React from 'react';
import { resolve } from 'inversify-react';
import { observable, computed } from 'mobx';
import { observer } from 'mobx-react';
import { isArrayLike } from 'mobx';
import * as FileSaver from "file-saver";

import { ToastNotifications } from 'src/core/ui/toast';
import { ControllerProps } from 'src/router';
import { ObjectUtils } from 'src/core/utils/object';
import { ContextService } from 'src/core/services/context.service';
import { ClientGDPRItemDataService } from './client-gdpr-form.data';
import { QuoteGDPRDataService } from './quote-gdpr-form.data';
import { QuotePaxGDPRDataService } from './quote-pax-gdpr-form.data';
import { ClientGDPRFormView } from './client-gdpr-form.view'
import { FormatService } from 'src/core/services/format.service';

interface Props extends ControllerProps<{ id: string }> {
}

@observer
export class ClientGDPRFormController extends React.Component<Props, null>  {
	@observable
	dataService: ClientGDPRItemDataService;

	@observable
	quoteDataService: QuoteGDPRDataService;

	@observable
	quotePaxDataService: QuotePaxGDPRDataService

	@resolve
	formatService: FormatService;

	@resolve
	contextService: ContextService;

	@resolve
	toast: ToastNotifications;

	componentDidMount() {
		this.props.setBusy(true);
		this.loadData().
			catch((error) => {
				this.toast.showToast('Request failed', ObjectUtils.parseError(error), 'error');
			}).then(() => {
				this.props.setBusy(false);
			});


	}

	private async loadData(): Promise<void> {
		this.dataService = ClientGDPRItemDataService.make(this.contextService);
		this.quoteDataService = QuoteGDPRDataService.make(this.contextService);
		this.quotePaxDataService = QuotePaxGDPRDataService.make(this.contextService);
		// Load entity
		this.dataService.addFilter('id', 'eq', this.props.match.params.id);
		this.dataService.addSorting('id', 'asc', true);
		this.dataService.pageSize = 1;
		await this.dataService.load(query => query);
		const clientId = parseInt(this.props.match.params.id);
		await this.loadRelationship(this.quoteDataService, 'clientId', clientId, 25, 'id');
		if (this.quoteDataService.items && this.quoteDataService.items.length>0)
			await this.loadRelationship(this.quotePaxDataService, 'quoteId', this.quoteDataService.items.map(q=>q.id), 25, 'id');
	}
	
	private async loadRelationship(relationshipDataService, prop: string, value: number|number[], pageSize: number, sortBy: string = 'id'): Promise<void> {
		//relationshipDataService.addFilter(prop, 'eq', value);
		relationshipDataService.addSorting(sortBy, 'asc', true);
		relationshipDataService.pageSize = pageSize;
		 await relationshipDataService.load(function (entitySet) {
			if (value != null) {
				const values = isArrayLike(value) ? value : [value];
				const predicate = values.reduce<string>((prev, curr) => (prev ? (prev + ' || ') : '') + `it.${prop} == ${curr}`, null);
				return entitySet.filter(predicate);
			}
			return entitySet;
		});
	}

	@computed
	get title() {
		if (this.dataService && this.dataService.item) {
			return `Client ${this.dataService.item.displayName}`;
		}
		return '';
	}
	
	downloadClientJson = (ev?: React.MouseEvent<HTMLButtonElement>) => {
		let model: PersonItem = this.dataService.item.dynamicProperties.person;
		let client = {
			firstName: model.firstName,
			lastName: model.lastName,
			middleName: model.middleName,
			birthDate: model.birthDate,
			email: this.dataService.item.dynamicProperties.email,
			contacts: this.dataService.item.dynamicProperties.contacts? this.dataService.item.dynamicProperties.contacts.map(c => ({
				displayName: c.displayName,
				postalCode: c.postalCode,
				city: c.city,
				county: c.county,
				address: c.address,
				additionalAddress: c.additionalAddress,
				phones: c.phones? c.phones.map(t => (
					{
						type: t.type,
						number: t.number
					})):[],
				emails: c.emails? c.emails.map(e => (
					{
						type: e.type,
						email: e.email
					})):[],
			})):[],
			quotes: this.quoteDataService.items.map(q => ({
				quoteNumber: q.quoteNumber,
				dateFrom: q.dateFrom,
				dateTo: q.dateTo,
				status: q.statusText,
				agent: q.agentName,
				destination: q.travelRegionCode,
				paxs: this.quotePaxDataService.items.filter(p => p.quoteId == q.id).map(p => ({
					salutation: p.dynamicProperties.salutation,
					foreName: p.dynamicProperties.foreName,
					MiddleName: p.dynamicProperties.MiddleName,
					surName: p.dynamicProperties.surName,
					nationality: p.dynamicProperties.nationality,
					birthDate: p.dynamicProperties.birthDate,
					gender: p.dynamicProperties.gender
				}))
			})),

		};
		const content = ObjectUtils.toString(client);
		var file = new Blob([content], { type: 'application/json' });
		FileSaver.saveAs(file, `${this.dataService.item.displayName.trim()}.json`);
	}

	handleCancel = (): void => {
		this.props.history.push('/crm/gdpr');
	}

	anonymitation = async () => {
		try {
			this.props.setBusy(true);
			let ctx = await this.contextService.current();
			let clientId = this.dataService.item.id;
			let result = await ctx.Anonymitation(clientId, false);
			if (result)
				this.props.history.push('/crm/gdpr');
			else
				this.toast.showToast('Validation error!', 'Error deleting client information', 'error');
			this.props.setBusy(false);
		}
		catch (error) {
			this.props.setBusy(false);
			this.toast.showToast('Request failed', ObjectUtils.parseError(error), 'error');
		}
	}

	render() {
		if (this.dataService == null || this.dataService.item == null || this.quoteDataService == null) {
			return null;
		}

		return (
			<ClientGDPRFormView
				anonymitation={this.anonymitation}
				quoteDataService={this.quoteDataService}
				quotePaxDataService={this.quotePaxDataService}
				formatService={this.formatService}
				title={this.title}
				cancel={this.handleCancel}
				model={this.dataService.item}
				contextService={this.contextService}
				setBusy={this.props.setBusy}
				downloadClientJson={this.downloadClientJson}
			/>
		);
	}
}



// WEBPACK FOOTER //
// ./src/app/gdpr/clients/form/client-gdpr-form.controller.tsx